import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import {
    Apple, Moon, Dumbbell, HeartPulse, Droplets, Flame,
    TrendingUp, Trophy, Zap, Clock, Target, BarChart3
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const periods = [
    { key: 'week', de: 'Woche', en: 'Week' },
    { key: 'month', de: 'Monat', en: 'Month' },
    { key: 'ytd', de: 'YTD', en: 'YTD' },
    { key: 'year', de: 'Jahr', en: 'Year' },
];

// Map sparkline metric key → DOM section to focus
const METRIC_FOCUS_MAP = {
    calories: 'stats-nutrition',
    water: 'stats-nutrition',
    sleep: 'stats-sleep',
    steps: 'stats-fitness',
    workouts: 'stats-fitness',
    mood: 'stats-mood',
};

export default function Statistics() {
    const { language } = useLanguage();
    const { user } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();
    const queryPeriod = searchParams.get('period');
    const focusMetric = searchParams.get('focus');
    const [period, setPeriod] = useState(['week', 'month', 'ytd', 'year'].includes(queryPeriod) ? queryPeriod : 'week');
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [highlightId, setHighlightId] = useState(null);
    const sectionsRef = useRef({});

    useEffect(() => {
        loadStats();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [period]);

    // Scroll to focused section once data is loaded
    useEffect(() => {
        if (loading || !focusMetric) return;
        const sectionId = METRIC_FOCUS_MAP[focusMetric];
        if (!sectionId) return;
        const el = sectionsRef.current[sectionId];
        if (el) {
            setTimeout(() => {
                el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                setHighlightId(sectionId);
                // Auto-clear highlight after 2.5s
                setTimeout(() => setHighlightId(null), 2500);
            }, 200);
        }
    }, [loading, focusMetric]);

    const loadStats = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API}/api/dashboard/statistics?period=${period}`);
            setData(res.data);
        } catch (e) {
            console.error('Stats load error:', e);
        } finally {
            setLoading(false);
        }
    };

    const t = language === 'de';

    return (
        <div className="p-4 lg:p-8 pb-24 lg:pb-8" data-testid="statistics-page">
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="max-w-4xl mx-auto space-y-4"
            >
                <div className="flex items-center justify-between">
                    <h1 className="font-display text-2xl lg:text-3xl font-bold flex items-center gap-2">
                        <BarChart3 className="w-7 h-7 text-primary" />
                        {t ? 'Übersicht' : 'Overview'}
                    </h1>
                </div>

                {/* Period Selector */}
                <div className="flex gap-2 bg-muted p-1 rounded-2xl" data-testid="period-selector">
                    {periods.map((p) => (
                        <button
                            key={p.key}
                            onClick={() => setPeriod(p.key)}
                            className={`flex-1 py-2.5 px-3 rounded-xl text-sm font-semibold transition-all ${
                                period === p.key
                                    ? 'bg-primary text-primary-foreground shadow-md'
                                    : 'text-muted-foreground hover:text-foreground'
                            }`}
                            data-testid={`period-${p.key}`}
                        >
                            {t ? p.de : p.en}
                        </button>
                    ))}
                </div>

                {loading ? (
                    <div className="space-y-4">
                        {[1,2,3].map(i => <div key={i} className="h-32 bg-muted animate-pulse rounded-2xl" />)}
                    </div>
                ) : data ? (
                    <div className="space-y-4">
                        {/* Gamification Banner */}
                        <Card className="border">
                            <CardContent className="p-4">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <Trophy className="w-6 h-6 text-amber-500" />
                                        <div>
                                            <p className="font-bold text-lg">Level {data.gamification.level}</p>
                                            <p className="text-sm text-muted-foreground">{data.gamification.total_points} {t ? 'Punkte' : 'Points'}</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Zap className="w-5 h-5 text-orange-500" />
                                        <span className="font-bold">{data.gamification.streak}</span>
                                        <span className="text-sm text-muted-foreground">{t ? 'Tage' : 'days'}</span>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        {/* Nutrition */}
                        <Card
                            ref={(el) => (sectionsRef.current['stats-nutrition'] = el)}
                            className={`module-nutrition border transition-all duration-500 ${highlightId === 'stats-nutrition' ? 'ring-2 ring-primary ring-offset-2 ring-offset-background shadow-lg' : ''}`}
                            data-testid="stats-nutrition"
                        >
                            <CardHeader className="pb-2">
                                <CardTitle className="text-base flex items-center gap-2">
                                    <Apple className="w-5 h-5 icon-nutrition" />
                                    {t ? 'Ernährung' : 'Nutrition'}
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-3">
                                <div className="grid grid-cols-2 gap-3">
                                    <Stat icon={<Flame className="w-4 h-4 icon-fitness" />} label={t ? 'Ø Kalorien/Tag' : 'Avg Calories/Day'} value={data.nutrition.avg_daily_calories} unit="kcal" />
                                    <Stat icon={<Droplets className="w-4 h-4 icon-travel" />} label={t ? 'Ø Wasser/Tag' : 'Avg Water/Day'} value={Math.round(data.nutrition.avg_daily_water_ml / 100) / 10} unit="L" />
                                </div>
                                <div className="grid grid-cols-3 gap-2 pt-2 border-t border-border">
                                    <MiniStat label={t ? 'Mahlzeiten' : 'Meals'} value={data.nutrition.meals_logged} />
                                    <MiniStat label={t ? 'Protein ges.' : 'Total Protein'} value={`${data.nutrition.total_protein_g}g`} />
                                    <MiniStat label={t ? 'Wasser ges.' : 'Total Water'} value={`${(data.nutrition.total_water_ml / 1000).toFixed(1)}L`} />
                                </div>
                            </CardContent>
                        </Card>

                        {/* Sleep */}
                        <Card
                            ref={(el) => (sectionsRef.current['stats-sleep'] = el)}
                            className={`module-sleep border transition-all duration-500 ${highlightId === 'stats-sleep' ? 'ring-2 ring-primary ring-offset-2 ring-offset-background shadow-lg' : ''}`}
                            data-testid="stats-sleep"
                        >
                            <CardHeader className="pb-2">
                                <CardTitle className="text-base flex items-center gap-2">
                                    <Moon className="w-5 h-5 icon-sleep" />
                                    {t ? 'Schlaf' : 'Sleep'}
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-3">
                                <div className="grid grid-cols-2 gap-3">
                                    <Stat icon={<Clock className="w-4 h-4 icon-sleep" />} label={t ? 'Ø Dauer' : 'Avg Duration'} value={data.sleep.avg_duration_h} unit="h" />
                                    <Stat icon={<Target className="w-4 h-4 icon-fitness" />} label={t ? 'Ø Qualität' : 'Avg Quality'} value={data.sleep.avg_quality} unit="/10" />
                                </div>
                                <div className="grid grid-cols-3 gap-2 pt-2 border-t border-border">
                                    <MiniStat label={t ? 'Einträge' : 'Entries'} value={data.sleep.entries} />
                                    <MiniStat label={t ? 'Beste Nacht' : 'Best Night'} value={`${data.sleep.best_night_h}h`} />
                                    <MiniStat label={t ? 'Kürzeste' : 'Shortest'} value={`${data.sleep.worst_night_h}h`} />
                                </div>
                            </CardContent>
                        </Card>

                        {/* Fitness */}
                        <Card
                            ref={(el) => (sectionsRef.current['stats-fitness'] = el)}
                            className={`module-fitness border transition-all duration-500 ${highlightId === 'stats-fitness' ? 'ring-2 ring-primary ring-offset-2 ring-offset-background shadow-lg' : ''}`}
                            data-testid="stats-fitness"
                        >
                            <CardHeader className="pb-2">
                                <CardTitle className="text-base flex items-center gap-2">
                                    <Dumbbell className="w-5 h-5 icon-fitness" />
                                    Fitness
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="grid grid-cols-2 gap-3">
                                    <Stat icon={<Dumbbell className="w-4 h-4 icon-fitness" />} label={t ? 'Workouts' : 'Workouts'} value={data.fitness.total_workouts} unit="" />
                                    <Stat icon={<Flame className="w-4 h-4 icon-health" />} label={t ? 'Verbrannt' : 'Burned'} value={data.fitness.total_calories_burned} unit="kcal" />
                                </div>
                                <div className="grid grid-cols-2 gap-2 pt-3 mt-3 border-t border-border">
                                    <MiniStat label={t ? 'Gesamtzeit' : 'Total Time'} value={`${data.fitness.total_minutes} min`} />
                                    <MiniStat label={t ? 'Ø pro Woche' : 'Avg/Week'} value={data.fitness.avg_per_week} />
                                </div>
                            </CardContent>
                        </Card>

                        {/* Health */}
                        {data.health.symptoms_logged > 0 && (
                            <Card className="module-health border">
                                <CardHeader className="pb-2">
                                    <CardTitle className="text-base flex items-center gap-2">
                                        <HeartPulse className="w-5 h-5 icon-health" />
                                        {t ? 'Gesundheit' : 'Health'}
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="grid grid-cols-2 gap-3">
                                        <Stat icon={<HeartPulse className="w-4 h-4 icon-health" />} label={t ? 'Symptome' : 'Symptoms'} value={data.health.symptoms_logged} unit="" />
                                        <Stat icon={<Target className="w-4 h-4" />} label={t ? 'Ø Schwere' : 'Avg Severity'} value={data.health.avg_severity} unit="/10" />
                                    </div>
                                </CardContent>
                            </Card>
                        )}

                        {/* Mood Distribution */}
                        {data.mood.entries > 0 && (
                            <Card
                                ref={(el) => (sectionsRef.current['stats-mood'] = el)}
                                className={`border transition-all duration-500 ${highlightId === 'stats-mood' ? 'ring-2 ring-primary ring-offset-2 ring-offset-background shadow-lg' : ''}`}
                                data-testid="stats-mood"
                            >
                                <CardHeader className="pb-2">
                                    <CardTitle className="text-base">{t ? 'Stimmung' : 'Mood'}</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="flex gap-2 justify-around">
                                        {[
                                            { key: 'great', emoji: '😄', label: 'Super' },
                                            { key: 'good', emoji: '🙂', label: 'Gut' },
                                            { key: 'okay', emoji: '😐', label: 'Okay' },
                                            { key: 'meh', emoji: '😕', label: 'Meh' },
                                            { key: 'bad', emoji: '😢', label: 'Schlecht' },
                                        ].map(m => (
                                            <div key={m.key} className="text-center">
                                                <span className="text-2xl">{m.emoji}</span>
                                                <p className="text-lg font-bold">{data.mood.distribution[m.key] || 0}</p>
                                                <p className="text-[10px] text-muted-foreground">{m.label}</p>
                                            </div>
                                        ))}
                                    </div>
                                </CardContent>
                            </Card>
                        )}
                    </div>
                ) : null}
            </motion.div>
        </div>
    );
}

function Stat({ icon, label, value, unit }) {
    return (
        <div className="flex items-center gap-3 p-3 bg-card rounded-xl border border-border">
            <div className="shrink-0">{icon}</div>
            <div>
                <p className="text-xs text-muted-foreground">{label}</p>
                <p className="text-xl font-bold">{value}<span className="text-sm font-normal text-muted-foreground ml-1">{unit}</span></p>
            </div>
        </div>
    );
}

function MiniStat({ label, value }) {
    return (
        <div className="text-center">
            <p className="text-lg font-bold">{value}</p>
            <p className="text-[10px] text-muted-foreground uppercase">{label}</p>
        </div>
    );
}
